import { useState, useEffect, useRef, useCallback } from "react";
import { LeaderboardParticipant } from "@/hooks/useLeaderboard";
import { motion, transitions } from "@/lib/animations";

const DISMISS_THRESHOLD = 150;
const ANIMATION_DURATION = 300;

interface ChallengeContext {
  name: string;
  totalDistanceGoal: number;
}

interface ParticipantProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  participant: LeaderboardParticipant | null;
  challengeContext: ChallengeContext;
}

type CloseType = "none" | "button" | "drag";

export function ParticipantProfileModal({
  isOpen,
  onClose,
  participant,
  challengeContext,
}: ParticipantProfileModalProps) {
  const [closeType, setCloseType] = useState<CloseType>("none");
  const [dragY, setDragY] = useState(0);
  const [canDrag, setCanDrag] = useState(false);
  const [progressMounted, setProgressMounted] = useState(false);
  const dragRef = useRef({ active: false, startY: 0 });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCloseType("none");
      setDragY(0);
      setCanDrag(false);
      setProgressMounted(false);
      dragRef.current = { active: false, startY: 0 };
    }
  }, [isOpen]);

  // Trigger progress animation after modal slide-up completes
  useEffect(() => {
    if (canDrag && !progressMounted) {
      setProgressMounted(true);
    }
  }, [canDrag, progressMounted]);

  const handleClose = useCallback(
    (type: "button" | "drag" = "button") => {
      if (closeType !== "none") return;
      setCloseType(type);
      if (type === "drag") {
        setDragY(window.innerHeight);
      }
      setTimeout(onClose, ANIMATION_DURATION);
    },
    [closeType, onClose]
  );

  // Pointer event handlers for drag-to-dismiss
  const handlePointerDown = (e: React.PointerEvent) => {
    if (!canDrag) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { active: true, startY: e.clientY };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current.active) return;
    e.preventDefault();
    const newDragY = Math.max(0, e.clientY - dragRef.current.startY);
    setDragY(newDragY);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current.active) return;
    e.preventDefault();
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    dragRef.current.active = false;

    if (dragY > DISMISS_THRESHOLD) {
      handleClose("drag");
    } else {
      setDragY(0);
    }
  };

  if (!isOpen || !participant) return null;

  const isClosing = closeType !== "none";
  const isDragging = dragRef.current.active;

  // Calculate progress values
  const distanceCompleted = participant.totalRunDistance;
  const totalGoal = challengeContext.totalDistanceGoal;
  const distanceRemaining = Math.max(0, totalGoal - distanceCompleted);
  const progressPercentage =
    totalGoal > 0 ? Math.min(100, (distanceCompleted / totalGoal) * 100) : 0;

  // Medal colors for rank badge
  const getMedalColor = (rank: number) => {
    switch (rank) {
      case 1:
        return "bg-[#FFD700] text-[#0c1c0c]"; // Gold
      case 2:
        return "bg-[#C0C0C0] text-[#0c1c0c]"; // Silver
      case 3:
        return "bg-[#CD7F32] text-white"; // Bronze
      default:
        return "bg-primary/20 text-primary";
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isClosing ? "opacity-0" : "opacity-100"
        }`}
        style={{
          opacity: dragY > 0 ? Math.max(0, 1 - dragY / 300) : undefined,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose("button");
        }}
      />

      {/* Modal - Bottom sheet style */}
      <div
        className="fixed inset-x-0 bottom-0 z-50 flex justify-center sm:items-center sm:inset-0 sm:p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`w-full max-w-[440px] bg-[#1a2e1a]/95 sm:rounded-[2.5rem] rounded-t-[2.5rem] relative flex flex-col shadow-2xl ring-1 ring-white/10 overflow-hidden ${
            closeType === "drag"
              ? ""
              : closeType === "button"
              ? "animate-slide-down"
              : canDrag
              ? ""
              : "animate-slide-up"
          }`}
          style={{
            transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
            transition: isDragging ? "none" : "transform 0.3s ease-out",
          }}
          onAnimationEnd={() => {
            if (!isClosing) setCanDrag(true);
          }}
        >
          {/* Close Button */}
          <button
            onClick={() => handleClose("button")}
            className="absolute top-4 right-4 z-10 flex size-8 items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors text-white/60 hover:text-white"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>

          {/* Drag Handle */}
          <div
            className="flex justify-center pt-3 pb-1 shrink-0 cursor-grab active:cursor-grabbing select-none sm:hidden"
            style={{ touchAction: "none" }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <div className="h-1 w-10 rounded-full bg-white/20 pointer-events-none" />
          </div>

          {/* Header with gradient background */}
          <div className="flex flex-col items-center pt-6 pb-4 bg-gradient-to-b from-white/5 to-transparent">
            {/* Avatar with Rank Badge */}
            <div className="relative mb-3">
              <img
                src={participant.avatar}
                alt={participant.name}
                className="w-20 h-20 rounded-full object-cover border-4 border-primary/30"
              />
              {/* Rank Badge */}
              <div
                className={`absolute -bottom-2 left-1/2 -translate-x-1/2 ${getMedalColor(
                  participant.rank
                )} rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-lg`}
              >
                {participant.rank}
              </div>
            </div>

            {/* Name */}
            <h3 className="text-xl font-bold text-white mt-1">
              {participant.name}
            </h3>

            {/* Challenge Name */}
            <p className="text-xs font-medium text-white/50 uppercase tracking-wider mt-1">
              {challengeContext.name}
            </p>
          </div>

          {/* Content */}
          <div className="px-6 pb-8 space-y-5">
            {/* Goal Progress Card */}
            <div className="bg-white/5 rounded-2xl p-4 border border-white/5 relative overflow-hidden">
              {/* Decorative glow */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-sm font-medium text-white/80">
                      Goal Progress
                    </span>
                    <span className="text-lg font-bold text-primary glow-text tabular-nums">
                      {progressPercentage.toFixed(0)}%
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="h-2.5 w-full bg-white/10 rounded-full relative overflow-hidden">
                    <motion.div
                      className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-green-900 via-primary to-primary shadow-[0_0_15px_rgba(13,242,13,0.5)]"
                      initial={{ width: "0%" }}
                      animate={{ width: progressMounted ? `${progressPercentage}%` : "0%" }}
                      transition={transitions.progress}
                    >
                      {/* Glow marker at end */}
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-4 h-8 bg-white/40 blur-md rounded-full" />
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-px w-1 h-2.5 bg-white rounded-full shadow-[0_0_8px_white]" />
                    </motion.div>
                  </div>
                </div>

                {/* Trophy Icon */}
                <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20 shrink-0">
                  <span className="material-symbols-outlined text-primary text-2xl">
                    trophy
                  </span>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Distance Completed */}
              <div className="bg-white/5 p-4 rounded-4xl border border-white/5 flex flex-col items-center text-center gap-2 hover:bg-white/10 transition-colors">
                <div className="size-8 rounded-full bg-blue-500/20 flex items-center justify-center mb-1">
                  <span
                    className="material-symbols-outlined text-blue-400 text-sm"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    sprint
                  </span>
                </div>
                <div>
                  <p className="text-xl font-extrabold text-white">
                    {distanceCompleted.toFixed(1)} km
                  </p>
                  <p className="text-xs text-white/50 font-medium">Completed</p>
                </div>
              </div>

              {/* Distance Remaining */}
              <div className="bg-white/5 p-4 rounded-4xl border border-white/5 flex flex-col items-center text-center gap-2 hover:bg-white/10 transition-colors">
                <div className="size-8 rounded-full bg-[#FF007F]/20 flex items-center justify-center mb-1">
                  <span
                    className="material-symbols-outlined text-[#FF007F] text-sm"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    flag
                  </span>
                </div>
                <div>
                  <p className="text-xl font-extrabold text-white">
                    {distanceRemaining.toFixed(1)} km
                  </p>
                  <p className="text-xs text-[#FF007F] font-medium">To Go!</p>
                </div>
              </div>
            </div>

            {/* Streak Message */}
            {participant.currentStreak > 0 && (
              <div className="text-center">
                <p className="text-xs text-white/40 italic">
                  "{participant.name} is on a {participant.currentStreak}-day
                  streak!"
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
